import { ArrowUpRight, MessageCircle } from "lucide-react";
import { ChapterTag, FadeUp, WA_LINK } from "@/components/Reveal";

const BRANDS = [
  {
    id: "taste-ride",
    n: "B.01",
    name: "Taste Ride",
    tag: "Flagship Brand",
    cuisine: "Signature North Indian & Tandoor classics",
    img: "/images/biryani.jpg",
    span: "md:col-span-4",
  },
  {
    id: "andhra-biryani-co",
    n: "B.02",
    name: "Andhra Biryani Co",
    tag: "Volume Driver",
    cuisine: "Fiery Hyderabadi & Andhra dum biryanis",
    img: "/images/south.jpg",
    span: "md:col-span-2",
  },
  {
    id: "kulcha-co",
    n: "B.03",
    name: "Kulcha & Co",
    tag: "High Repeat Rate",
    cuisine: "Amritsari stuffed kulchas & chole",
    span: "md:col-span-2",
    typographic: true,
  },
  {
    id: "chinese-bowl",
    n: "B.04",
    name: "The Chinese Bowl",
    tag: "Late-Night Favourite",
    cuisine: "Indo-Chinese wok boxes, noodles & dimsums",
    img: "/images/chinese.jpg",
    span: "md:col-span-2",
  },
  {
    id: "urmi-foods",
    n: "B.05",
    name: "Urmi Foods",
    tag: "100% Pure Veg",
    cuisine: "Home-style thalis & satvik dishes",
    img: "/images/thali.jpg",
    span: "md:col-span-2",
  },
];

const BrandCard = ({ b, i }) => (
  <FadeUp delay={i * 0.08} className={b.span}>
    <div
      data-testid={`brand-card-${b.id}`}
      className={`group relative h-72 overflow-hidden rounded-3xl transition-[transform,box-shadow] duration-500 hover:-translate-y-2 hover:shadow-[0_28px_70px_rgba(224,90,43,0.25)] md:h-80 ${
        b.typographic ? "bg-gradient-to-br from-amber-400 via-saffron to-flame" : "bg-coal"
      }`}
    >
      {b.img && (
        <>
          <img
            src={b.img}
            alt={`${b.name} signature dish`}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian/95 via-obsidian/35 to-obsidian/10" />
        </>
      )}
      {b.typographic && (
        <span className="pointer-events-none absolute -right-4 -top-8 select-none font-display text-[7rem] font-bold leading-none text-obsidian/10">
          K&amp;Co
        </span>
      )}
      <div className="absolute left-5 top-5 flex items-center gap-2">
        <span className={`rounded-full px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] ${
          b.typographic ? "bg-obsidian text-saffron" : "bg-white/10 text-cream backdrop-blur-sm"
        }`}>
          {b.n}
        </span>
        <span className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${
          b.typographic ? "bg-obsidian/15 text-obsidian" : "bg-saffron/90 text-obsidian"
        }`}>
          {b.tag}
        </span>
      </div>
      <div className="absolute inset-x-5 bottom-5">
        <h3 className={`font-display text-2xl font-semibold tracking-tight sm:text-3xl ${
          b.typographic ? "text-obsidian" : "text-cream"
        }`}>
          {b.name}
        </h3>
        <p className={`mt-1.5 text-sm ${b.typographic ? "text-obsidian/70" : "text-slate-300"}`}>
          {b.cuisine}
        </p>
      </div>
    </div>
  </FadeUp>
);

export default function CloudKitchenChapter() {
  return (
    <section id="brands" data-testid="cloud-kitchen-chapter" className="relative bg-cream px-5 py-24 text-obsidian sm:px-8 lg:py-32">
      <div className="mx-auto max-w-7xl">
        <ChapterTag index="02" label="Cloud Kitchen Network" dark={false} />

        <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-12">
          <FadeUp className="lg:col-span-7">
            <h2 className="font-display text-2xl font-semibold leading-[1.12] tracking-tight sm:text-3xl lg:text-4xl" data-testid="cloud-kitchen-headline">
              Five brands. One kitchen.{" "}
              <span className="font-serif italic font-medium text-flame">Zero front-of-house cost.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.12} className="lg:col-span-5">
            <p className="text-base leading-relaxed text-slate-600">
              From a single shared kitchen, we run five specialised delivery brands — each built
              for a different craving, each ranking on Ranchi's delivery apps. Same chefs, same
              sourcing, five separate revenue streams.
            </p>
          </FadeUp>
        </div>

        <FadeUp delay={0.15} className="mt-10">
          <div className="flex flex-wrap items-center gap-3" data-testid="delivery-partner-strip">
            <span className="mr-2 font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500">Order on</span>
            <span className="rounded-full bg-[#E23744] px-6 py-2.5 font-display text-sm font-bold lowercase tracking-tight text-white" data-testid="delivery-zomato">
              zomato
            </span>
            <span className="rounded-full bg-[#FC8019] px-6 py-2.5 font-display text-sm font-bold lowercase tracking-tight text-white" data-testid="delivery-swiggy">
              swiggy
            </span>
            <a
              data-testid="delivery-whatsapp"
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2 rounded-full bg-wa px-6 py-2.5 font-display text-sm font-semibold text-obsidian transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(37,211,102,0.4)]"
            >
              <MessageCircle size={15} strokeWidth={2.5} />
              WhatsApp Direct
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </FadeUp>

        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-6" data-testid="brand-grid">
          {BRANDS.map((b, i) => (
            <BrandCard key={b.id} b={b} i={i} />
          ))}
        </div>

        <FadeUp delay={0.2} className="mt-10">
          <p className="text-center font-mono text-xs uppercase tracking-[0.3em] text-slate-500" data-testid="cloud-kitchen-note">
            One kitchen · Five brands · Infinite appetite
          </p>
        </FadeUp>
      </div>
    </section>
  );
}
